import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { isEqual } from 'lodash';
import { selectDefaultView, useSaveViewStore } from '../saveViewFeature';
import { type TableState, useTableData, useTableName } from '../utils';
import { Decoder } from './Decoder';

/**
 * Compares current form state to the default saved view (or schema defaults).
 * Avoids comparing encoded `f` blobs — re-encoding the same state can differ and cause UI blink.
 */
export const useIsDirtyView = () => {
  const tableName = useTableName();
  const defaultView = useSaveViewStore(selectDefaultView(tableName));
  const { defaultValues } = useTableData();
  const formValues = useWatch<TableState>();
  const [decodedDefaultView, setDecodedDefaultView] = useState<TableState | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!defaultView?.data) {
      setDecodedDefaultView(null);
      return;
    }

    void Decoder.decode(defaultView.data)
      .then((decoded) => {
        if (!cancelled) {
          setDecodedDefaultView(decoded);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDecodedDefaultView(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [defaultView?.data]);

  if (!formValues) {
    return false;
  }

  if (!defaultView) {
    return !isEqual(formValues, defaultValues);
  }

  // Wait for decode so we don't flash dirty while default view is loading.
  if (!decodedDefaultView) {
    return false;
  }

  return !isEqual(formValues, decodedDefaultView);
};
